import { useState, useEffect, useContext } from 'react';
import UserContext from "../../contexts/userContext";

import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter'
import { a11yDark } from 'react-syntax-highlighter/dist/cjs/styles/prism'

import Spinner from 'react-bootstrap/Spinner';
import { HTML } from '../HTML';
import { CodeCopyButton } from './1-2-1-1_CodeCopyButton';
import { getConversation } from '../navigation/2-1-1_ConversationCard';

import { scrollToBottom, retryFetch, generateConversationOverview, focusOnQueryInput } from '../../utils';
import { ModelDetail } from './1-2-1-2_ModelDetail';

export const MessageContent = ({message, showMessageDetail, history}) => {
    a11yDark['pre[class*="language-"]']['margin'] = 0;
    a11yDark['pre[class*="language-"]']['padding'] = '2em 1em 1em 1em';

    const { userContext, setUserContext, setAccount } = useContext(UserContext);
    const [ messageContent, setMessageContent] = useState(message.content);

    const documentid = userContext.document.documentid;
    const conversationid = userContext.conversation.conversationid;

    const generateResponseStreaming = (retryCount) => {
        console.log('querying LLM');
        const endpoint = process.env[`REACT_APP_GENERATE_RESPONSE_STREAMING_URL__${process.env["REACT_APP_DEPLOYMENT_TYPE"]}`];
        const params = {
            method: "POST",
            body: JSON.stringify({
                authorizationHeader: `Bearer ${userContext.account.idToken}`,
                model: userContext.model,
                documentid: documentid,
                conversationid: conversationid,
                humanInput: message.humanInput,
                context: message.context,
                promptTemplate: message.promptTemplate,
                clientResolvedPromptTemplate: message.clientResolvedPromptTemplate,
                isCommand: message.isCommand || false
            })
        };
        
        let streamedtext = '';
        fetch(endpoint, params)
            .then(response => {
                const reader = response.body.getReader();
                reader.read().then(function pump({done, value}) {
                    const newText = new TextDecoder().decode(value);
                    streamedtext += newText;
                    setMessageContent(streamedtext);
                    scrollToBottom(0, 'instant');
    
                    if (done) {
                        if (streamedtext.length === 0) {
                            console.log('received empty stream, so assuming session expired. reauthenticating.')
                            retryFetch(userContext, setAccount, retryCount||0, generateResponseStreaming, {})
                        } else {
                            if (message.setHasHumanInput) {
                                message.setHasHumanInput(false);
                            }

                            document.getElementById('submitQueryButton').disabled = false;
                            focusOnQueryInput();

                            const conversationOverview = {
                                conversationid: conversationid
                            }
                            getConversation(0, {userContext, setUserContext, setAccount, conversationOverview, generateConversationOverview});
                        }
                        return;
                    }
                    return reader.read().then(pump);
                });
            })
            .catch((e) => {
                console.log('error when submitting query', e);
            });
    }

    useEffect(() => {
        if (messageContent === 'STREAMING_PLACEHOLDER') {
            setMessageContent('WAITING_FOR_STREAMING_RESPONSE');
            generateResponseStreaming();
        }
    }, []);

    if (message.type === 'human') {
        const humanInput = message.humanInput;
        const humanInputWithContext = message.content.trim();
        const partiallyResolvedPromptTemplate = message.partiallyResolvedPromptTemplate;
        
        if (showMessageDetail) {
            const resolvedPromptTemplate = partiallyResolvedPromptTemplate ? (
                partiallyResolvedPromptTemplate.replaceAll('{{input}}', humanInputWithContext).replaceAll('{{history}}', history)
            ) : humanInput;
            
            return <>
                <ModelDetail message={message} />
                <HTML text={resolvedPromptTemplate || humanInput} />
            </>
        } else {
            return <HTML text={humanInput} />
        }
    } else {
        if (messageContent === 'STREAMING_PLACEHOLDER' || messageContent === 'WAITING_FOR_STREAMING_RESPONSE') {
            return <>
                <Spinner animation="grow" className="spinner-grow-xs m-1" />
                <Spinner animation="grow" className="spinner-grow-xs m-1" />
                <Spinner animation="grow" className="spinner-grow-xs m-1" />
            </>
        } else {
            return <>
                {showMessageDetail && <ModelDetail message={message} />}
                <ReactMarkdown
                    className="post-markdown"
                    rehypePlugins={[rehypeRaw]}
                    remarkPlugins={[remarkGfm]}
                    components={{
                        pre: ({ children }) => {
                            return(
                                <pre className="code-pre">
                                    <CodeCopyButton>{children}</CodeCopyButton>
                                    {children}
                                </pre>
                            );
                        },
                        code({ node, inline, className = "pre-code-block", children, ...props }) {
                            const match = /language-(\w+)/.exec(className || '') || ['language-text', 'text'];
                            
                            if (typeof inline === 'undefined') {
                                inline = false;
                                try {
                                    inline = node.position.start.line === node.position.end.line;
                                } catch(e) {}
                            }

                            if (!inline && match) {
                                return (
                                    <SyntaxHighlighter
                                        style={a11yDark}
                                        language={match[1]}
                                        PreTag="div"
                                        {...props}
                                    >
                                        {String(children).replace(/\n$/, '')}
                                    </SyntaxHighlighter>
                                );
                            } else {
                                return (
                                    <code className={className} style={a11yDark} {...props}>
                                        {children}
                                    </code>
                                );
                            }
                        }
                    }}
                >
                    {messageContent}
                </ReactMarkdown>
            </>
        } 
    }
}